/**
 * The Consultant usage tab of Settings (the 2026-10-06 next-phase plan, step 3: "the consultant's limits, read only
 * for the org"): how many messages a person and the whole org may send the AI casting consultant in a day, and how many
 * have been sent, the person's own and the org's. Sahas sets the limits on the platform console; nobody else's count
 * is shown. ConsultantLimitsView is pure; ConsultantLimits reads.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { useBriefLive } from "@/lib/api/brief";
import { useCopilotLimitsQuery, type CopilotMemberLimits } from "@/lib/api/copilot-limits";
import {
  consultantLimitsLines,
  consultantLimitsState,
  type ConsultantLimitsState,
} from "@/lib/settings/consultant-words";

export function ConsultantLimitsView({
  limits,
  orgName,
  state,
}: {
  limits?: CopilotMemberLimits;
  orgName: string;
  state: ConsultantLimitsState;
}) {
  let body: React.ReactNode;
  if (state === "ready" && limits) {
    body = (
      <ul data-consultant-limits="" className="flex flex-col gap-1.5 text-sm text-on-surface">
        {consultantLimitsLines(limits, orgName).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    );
  } else if (state === "not_live") {
    body = <p className="text-sm text-on-surface-muted">The consultant's limits are read on the live platform.</p>;
  } else if (state === "off") {
    body = <p className="text-sm text-on-surface-muted">The AI casting consultant is not switched on for this platform.</p>;
  } else if (state === "error") {
    body = (
      <p role="alert" className="text-sm text-danger">
        The limits could not be read. Try again in a moment.
      </p>
    );
  } else {
    body = <p className="text-sm text-on-surface-muted">Reading the limits.</p>;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>The consultant&apos;s daily limits</CardTitle>
        <CardDescription>Read only: Sahas sets them on the platform console.</CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

export function ConsultantLimits({ orgName }: { orgName: string }) {
  const live = useBriefLive();
  const q = useCopilotLimitsQuery();
  const state = consultantLimitsState({ live, data: q.data, error: q.error });
  return <ConsultantLimitsView limits={q.data} orgName={orgName} state={state} />;
}
